import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatPLN, formatDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { FileText, Plus, Search, X, Calendar as CalendarIcon } from "lucide-react";

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const nav = useNavigate();
  const debounceRef = useRef();

  const load = (params = {}) => {
    setLoading(true);
    api
      .get("/quotes", { params })
      .then((r) => setQuotes(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      load(params);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [search, dateFrom, dateTo]);

  const createNew = async () => {
    const r = await api.post("/quotes", { name: "Nowa wycena" });
    nav(`/wyceny/${r.data.id}`);
  };

  const clearFilters = () => {
    setSearch("");
    setDateFrom("");
    setDateTo("");
  };

  const hasFilters = search || dateFrom || dateTo;

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--text-muted)] mb-2">
            Wyceny
          </div>
          <h1 className="heading text-3xl sm:text-4xl font-semibold tracking-tight">
            Twoje wyceny
          </h1>
          <p className="text-sm text-[var(--text-muted)] mt-2">
            Szablon do szybkiego liczenia ofert dla klientów.
          </p>
        </div>
        <Button
          onClick={createNew}
          data-testid="new-quote-btn"
          className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
        >
          <Plus size={16} className="mr-2" />
          Nowa wycena
        </Button>
      </header>

      <div className="surface p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-end" data-testid="quotes-filters">
        <div className="md:col-span-5">
          <label className="text-xs text-[var(--text-muted)] uppercase tracking-wide block mb-1.5">
            Szukaj po nazwie
          </label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="np. kuchnia, szafa..."
              className="pl-9"
              data-testid="filter-search"
            />
          </div>
        </div>
        <div className="md:col-span-3">
          <label className="text-xs text-[var(--text-muted)] uppercase tracking-wide block mb-1.5">
            Od daty
          </label>
          <div className="relative">
            <CalendarIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none" />
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="pl-9"
              data-testid="filter-date-from"
            />
          </div>
        </div>
        <div className="md:col-span-3">
          <label className="text-xs text-[var(--text-muted)] uppercase tracking-wide block mb-1.5">
            Do daty
          </label>
          <div className="relative">
            <CalendarIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none" />
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="pl-9"
              data-testid="filter-date-to"
            />
          </div>
        </div>
        <div className="md:col-span-1">
          {hasFilters && (
            <Button
              variant="outline"
              onClick={clearFilters}
              className="w-full"
              data-testid="filter-clear"
              title="Wyczyść filtry"
            >
              <X size={14} />
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="surface p-10 text-center text-[var(--text-muted)]">Wczytywanie...</div>
      ) : quotes.length === 0 ? (
        <div className="surface p-12 text-center" data-testid="quotes-empty">
          <div className="w-14 h-14 rounded-full bg-[var(--surface-2)] mx-auto mb-4 flex items-center justify-center">
            <FileText className="text-[var(--primary)]" size={22} />
          </div>
          <div className="heading text-lg font-semibold">
            {hasFilters ? "Brak wycen pasujących do filtrów" : "Brak wycen"}
          </div>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            {hasFilters ? "Spróbuj zmienić kryteria wyszukiwania." : "Utwórz pierwszą wycenę, aby szybko skalkulować ofertę."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="quotes-list">
          {quotes.map((q) => (
            <Link
              key={q.id}
              to={`/wyceny/${q.id}`}
              data-testid={`quote-card-${q.id}`}
              className="tab-action surface p-5"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="heading text-lg font-semibold">{q.name}</div>
                <span className="text-xs text-[var(--text-muted)]">
                  {formatDate(q.created_at)}
                </span>
              </div>
              {q.client_name && (
                <div className="text-sm text-[var(--text-muted)] mb-2">
                  {q.client_name}
                  {q.client_phone && ` · ${q.client_phone}`}
                </div>
              )}
              <div className="grid grid-cols-3 gap-2 pt-3 border-t border-[var(--border)] text-sm">
                <div>
                  <div className="text-xs text-[var(--text-muted)]">Koszty</div>
                  <div className="font-semibold tabular-nums">{formatPLN(q.total_costs)}</div>
                </div>
                <div>
                  <div className="text-xs text-[var(--text-muted)]">Cena</div>
                  <div className="font-semibold tabular-nums">{formatPLN(q.sale_price)}</div>
                </div>
                <div>
                  <div className="text-xs text-[var(--text-muted)]">Zarobek</div>
                  <div
                    className="font-semibold tabular-nums"
                    style={{
                      color: Number(q.profit) >= 0 ? "var(--success)" : "var(--danger)",
                    }}
                  >
                    {formatPLN(q.profit)}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
